/**
 * @file src/lib/gcsUpload.js
 * @description Browser → GCS multipart upload via the backend's
 * /api/s3/{initiate,sign-part,complete,abort} endpoints (GCS S3-compatible
 * XML API). ZERO AWS dependency.
 *
 * Replaces the legacy AWS pipeline (AWS API Gateway Lambda presign → AWS S3
 * bucket `storage-popuppushnewaccount` → storage.popuppush.com CloudFront).
 * The file now goes: browser → presigned GCS URL → our GCS bucket, and we
 * store the public GCS URL (https://storage.googleapis.com/<bucket>/<key>).
 *
 * The bucket is resolved SERVER-SIDE from AWS_BUCKET — do NOT send a bucket
 * override, S3MultipartController (assertScope) rejects mismatches. The
 * returned public URL is derived from the presigned part URL so it stays
 * correct across dev/prod without hardcoding any bucket or domain.
 */
import axios from "axios"; // raw axios for the cross-origin PUT straight to GCS
import apiClient from "../config/Axios"; // app instance: baseURL + httpOnly cookie auth

// GCS/S3 multipart requires every part except the last to be >= 5 MiB.
const PART_SIZE = 8 * 1024 * 1024; // 8 MiB

/**
 * Upload a File/Blob to our GCS bucket and return its public URL.
 *
 * @param {File|Blob} file               the file to upload
 * @param {string} key                   object key, MUST start with "<company_id>/"
 * @param {(percent:number)=>void} [onProgress]  0..100 across the whole file
 * @returns {Promise<string>}            public URL of the uploaded object
 */
export async function uploadToGcs(file, key, onProgress) {
  const cleanKey = key.replace(/\s/g, "");
  const contentType = file.type || "application/octet-stream";

  // 1) Start the multipart upload (bucket chosen server-side from AWS_BUCKET).
  // `size` lets the backend reject an over-quota upload BEFORE any bytes move
  // (S3MultipartController::initiate). It is a hint, not a control — complete()
  // re-checks the object that actually landed, so understating it just wastes
  // the upload.
  const { data: init } = await apiClient.post("/s3/initiate", {
    key: cleanKey,
    contentType,
    size: file.size,
  });
  const uploadId = init.uploadId;

  const totalParts = Math.max(1, Math.ceil(file.size / PART_SIZE));
  const parts = [];
  let publicBase = null;
  let uploadedBytes = 0;

  try {
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * PART_SIZE;
      const end = Math.min(start + PART_SIZE, file.size);
      const chunk = file.slice(start, end);

      // 2) Presigned PUT URL for this part.
      const { data: signed } = await apiClient.post("/s3/sign-part", {
        key: cleanKey,
        uploadId,
        partNumber,
      });

      // Derive the public object URL from the signed URL (origin + path,
      // query stripped). Env-agnostic — no hardcoded bucket or domain.
      if (!publicBase) {
        const u = new URL(signed.url);
        publicBase = `${u.origin}${u.pathname}`;
      }

      // 3) Upload the part straight to GCS. No credentials — this is a
      //    cross-origin request to storage.googleapis.com, not our API.
      const putRes = await axios.put(signed.url, chunk, {
        headers: { "Content-Type": contentType },
        transformRequest: [(d) => d], // keep the Blob/ArrayBuffer untouched
        onUploadProgress: (evt) => {
          if (!onProgress) return;
          const loaded = uploadedBytes + (evt.loaded || 0);
          onProgress(Math.min(99, Math.round((loaded / file.size) * 100)));
        },
      });

      // ETag is exposed by the bucket CORS (responseHeader "*"). Required to
      // complete the multipart upload.
      const etag = putRes.headers?.etag || putRes.headers?.ETag;
      if (!etag) {
        throw new Error(`Missing ETag header for part ${partNumber}`);
      }
      parts.push({ PartNumber: partNumber, ETag: etag });
      uploadedBytes += end - start;
    }

    // 4) Complete the upload.
    const { data: done } = await apiClient.post("/s3/complete", {
      key: cleanKey,
      uploadId,
      parts,
    });

    if (onProgress) onProgress(100);

    // Use the URL derived from the signed request (stable, path-style GCS).
    // The S3 SDK's completeMultipartUpload `location` comes back as http://,
    // which would be blocked as mixed content on our https site — so we
    // always normalise to https.
    const finalUrl = publicBase || done.location || "";
    return finalUrl.replace(/^http:\/\//i, "https://");
  } catch (err) {
    // Best-effort cleanup so we don't leave a dangling multipart upload.
    try {
      await apiClient.post("/s3/abort", { key: cleanKey, uploadId });
    } catch (_) {
      /* ignore abort failures */
    }
    throw err;
  }
}
